import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {NgClass} from '@angular/common';
import {MatProgressBar} from '@angular/material/progress-bar';
import {FileUploadService} from '../../control/file-upload-service';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';
import {LocalizationService} from '../../../localization/control/localization.service';
import {PresignedFileUrl, FileInfo} from '../../entity/form-file';

export interface FileUploadConfig {
  title: string;
  subtitle: string;
  icon: string;
  multiple: boolean;
  acceptedTypes: string[];
  maxSize: string;
  dropzoneText: string;
  optional?: boolean;
  actionName: string
}

export type UploadingStatus = {
  status: 'uploading' | 'completed' | 'error';
}

@Component({
  selector: 'app-file-uploads',
  imports: [
    MatIcon,
    NgClass,
    MatProgressBar,
    LocalizationPipePipe
  ],
  templateUrl: './file-uploads.html',
  standalone: true,
  styleUrl: './file-uploads.scss'
})
export class FileUploads implements OnInit, OnChanges {
  @Input() fieldName!: string;
  @Input() submissionId?: string | undefined;
  @Input() config: FileUploadConfig = {
    title: 'Photos/Documents',
    subtitle: 'Please upload photos/deed/layouts/...',
    icon: 'camera_alt',
    multiple: true,
    acceptedTypes: ['JPG', 'PNG', 'PDF', 'DOC', 'DOCX'],
    maxSize: '10MB per file',
    dropzoneText: 'fileUpload.dropzoneText',
    actionName: 'Upload'
  };
  stagingMode: boolean = false;
  @Input() required: boolean = false;
  uploading: boolean = false;
  uploaded: boolean = false;
  @Output() filesChange = new EventEmitter<File[]>();
  @Output() filesUploadingChange = new EventEmitter<UploadingStatus>();
  @Output() uploadSkipped = new EventEmitter<boolean>();
  @Output() validityChange = new EventEmitter<boolean>();
  @Output() stagedFilesChange = new EventEmitter<File[]>();

  private allowedFileExtensions: string[] = [];
  selectedFiles: File[] = [];
  isDragOver = false;
  protected errorMessages: string[] = [];
  protected hasError: boolean = false;

  constructor(
    private readonly fileUploadService: FileUploadService,
    private readonly cdr: ChangeDetectorRef,
    private readonly localizationService: LocalizationService) {
  }

  ngOnInit(): void {
    this.allowedFileExtensions = this.getAcceptAttribute().toLowerCase().split(',')
  }

  ngOnChanges(): void {
    this.allowedFileExtensions = this.getAcceptAttribute().toLowerCase().split(',')
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = Array.from(event.dataTransfer?.files || []);
    this.handleFiles(files);
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    this.handleFiles(files);
  }

  private handleFiles(files: File[]) {
    const errorMessages: string [] = []
    const filteredFiles = files.filter(e => {
      const strings = e.name.split('.');
      const splitElement = '.' + strings[strings.length - 1]?.toLowerCase();
      let b = this.allowedFileExtensions.includes(splitElement);
      if (!b) {
        errorMessages.push(`"${e.name}" ${this.localizationService.getTranslation('fileUpload.validation.invalidExtension')}`)
      }
      return b;
    });
    if (this.config.multiple) {
      this.selectedFiles.push(...filteredFiles);
    } else {
      this.selectedFiles = filteredFiles.slice(0, 1);
    }

    this.hasError = (files.length > filteredFiles.length);
    if (this.hasError) {
      this.errorMessages = [
        `${this.localizationService.getTranslation('fileUpload.validation.allowedExtensions')}: ${this.allowedFileExtensions.join(', ')}.`,
        ...errorMessages
      ];
    } else {
      this.errorMessages = []
    }

    this.emitChanges();
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.emitChanges();
  }

  private emitChanges() {
    this.filesChange.emit([...this.selectedFiles]);
    this.stagedFilesChange.emit([...this.selectedFiles]);
    this.validityChange.emit(this.isValid);
  }

  public get isValid(): boolean {
    return !this.required || this.selectedFiles.length > 0;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getAcceptAttribute(): string {
    const extensions = this.config.acceptedTypes.map(type => {
      switch (type.toUpperCase()) {
        case 'JPG':
          return '.jpg,.jpeg';
        default:
          return `.${type.toLowerCase()},.${type.toUpperCase()}`;
      }
    });
    return extensions
      .map(e => e.trim())
      .filter(e => e.length > 0)
      .join(',');
  }

  public getFileMetadata(): FileInfo[] {
    return this.selectedFiles.map(file => ({
      fileName: file.name,
      fileSizeInBytes: file.size,
      contentType: file.type,
    }));
  }

  public uploadWithPresignedUrls(presignedUrls: PresignedFileUrl[]): Promise<boolean> {
    if (this.selectedFiles.length === 0 || presignedUrls.length === 0) {
      return Promise.resolve(true);
    }

    this.uploading = true;
    this.errorMessages = [];

    return new Promise<boolean>((resolve) => {
      const fileCompleteProgress: Record<string, boolean> = {};
      let hasError = false;

      const updateOnCompletion = (r: Response, presignedURL: string, file: File) => {
        fileCompleteProgress[presignedURL] = r.ok;
        if (!r.ok) {
          hasError = true;
          this.errorMessages = [`${this.localizationService.getTranslation('fileUpload.validation.uploadError')}: ${file.name}`, ...this.errorMessages];
        }

        const allCompleted = Object.keys(fileCompleteProgress).length === presignedUrls.length &&
          Object.values(fileCompleteProgress).every(value => value !== undefined);

        if (allCompleted) {
          this.uploading = false;
          this.uploaded = !hasError;
          this.cdr.detectChanges();

          if (!hasError) {
            this.filesUploadingChange.emit({status: 'completed'});
          } else {
            this.filesUploadingChange.emit({status: 'error'});
          }

          resolve(!hasError);
        }
        this.cdr.detectChanges();
      };

      presignedUrls.forEach((presigned, i) => {
        const file = this.selectedFiles[i];
        fileCompleteProgress[presigned.signedUrl] = false;
        this.filesUploadingChange.emit({status: 'uploading'});
        this.fileUploadService.externalUpload(
          presigned.signedUrl,
          file,
          (r) => updateOnCompletion(r, presigned.signedUrl, file),
        );
      });
    });
  }
}
